import type { ReactNode } from 'react';

import { useShellNextStep } from '@shared/lib/appShell';

import { Button, ButtonLink } from '../Button/Button';

import styles from './NextStepAction.module.css';

interface CommonProps {
  /** Подпись — строкой, а не разметкой: она же уходит в полосу оболочки. */
  children: string;
  /**
   * Короткая форма той же подписи — только для полосы телефона.
   *
   * Нужна, когда полная подпись на 370 px занимает в полосе больше двух строк:
   * рядом с пузырём Karl кнопке остаётся около 200 px, и «Karl beauftragen:
   * Autohäuser nach Angaben fragen» вырастала в полосу на 107 px из 700.
   * Действие то же, формулировка короче; кнопка в потоке на телефоне скрыта,
   * поэтому две разные подписи одного действия рядом не стоят никогда.
   */
  dockLabel?: string | undefined;
  iconEnd?: ReactNode | undefined;
  className?: string | undefined;
}

type NextStepActionProps = CommonProps &
  ({ to: string; onClick?: undefined } | { onClick: () => void; to?: undefined });

/**
 * Главное действие экрана — переход к следующему шагу (FE-585, DESIGN_SYSTEM
 * §4.4). Красная кнопка, одна на экран.
 *
 * С 768 px она стоит в потоке, где её поставил экран. **На телефоне её несёт
 * оболочка** — в закреплённой снизу полосе рядом с пузырём Karl, — а здесь
 * она скрыта: две одинаковые красные кнопки на одном экране нарушили бы
 * правило «одна красная на экран» (§5.1). Вне оболочки (тест экрана)
 * полосы нет, и кнопка остаётся в потоке на любой ширине.
 *
 * Компонент только для переходов: ссылка на следующий экран или открытие окна
 * входа. Отправка формы и необратимое действие сюда не годятся — почему, см.
 * `ShellNextStep`.
 */
export function NextStepAction({
  children,
  dockLabel,
  iconEnd,
  className,
  ...action
}: NextStepActionProps) {
  const label = dockLabel ?? children;
  const docked = useShellNextStep(
    action.to === undefined ? { label, onClick: action.onClick } : { label, to: action.to },
  );
  const classes = [className, docked && styles.docked].filter(Boolean).join(' ');

  return action.to === undefined ? (
    <Button className={classes} iconEnd={iconEnd} onClick={action.onClick}>
      {children}
    </Button>
  ) : (
    <ButtonLink to={action.to} className={classes} iconEnd={iconEnd}>
      {children}
    </ButtonLink>
  );
}
